import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from 'lucide-react';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create WhatsApp message with form data
    const message = `Halo BTSSOFT,

Saya ingin konsultasi mengenai XAI ERP System:

Nama: ${formData.name}
Email: ${formData.email}
WhatsApp: ${formData.phone}
Bisnis: ${formData.company}

Pesan: ${formData.message}

Mohon hubungi saya untuk demo dan konsultasi lebih lanjut. Terima kasih!`;

    // Encode message for WhatsApp URL
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://api.whatsapp.com/send/?phone=6281218691110&text=${encodedMessage}&type=phone_number&app_absent=0`;
    
    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank');
    
    // Reset form
    setFormData({ name: '', email: '', phone: '', company: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-black relative overflow-hidden">
      {/* Glossy Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-tr from-green-600/5 via-transparent to-blue-600/5"></div>  
      <div className="absolute top-10 left-10 w-96 h-96 bg-green-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-green-900/30 text-green-300 rounded-full text-sm font-medium mb-6 border border-green-500/20">
            📞 Hubungi Kami
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Siap Memulai dengan 
            <span className="bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent"> XAI?</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Dapatkan konsultasi gratis dan demo eksklusif. 
            Tim expert kami siap membantu mengoptimalkan bisnis Anda.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="shadow-2xl shadow-black/40 bg-black/60 backdrop-blur-xl border border-white/10 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
            <div className="relative z-10">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-white">
                Konsultasi Gratis
              </CardTitle>
              <p className="text-gray-300">
                Isi form di bawah ini dan dapatkan demo personal dalam 24 jam
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Input
                      name="name"
                      placeholder="Nama Lengkap *"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-black/40 backdrop-blur-sm border-white/20 text-white placeholder-gray-400 focus:border-blue-400 focus:ring-blue-400 focus:bg-black/60 transition-all duration-300"
                    />
                  </div>
                  <div>
                    <Input
                      name="email"
                      type="email"
                      placeholder="Email *"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-black/40 backdrop-blur-sm border-white/20 text-white placeholder-gray-400 focus:border-blue-400 focus:ring-blue-400 focus:bg-black/60 transition-all duration-300"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Input
                      name="phone"
                      placeholder="No. WhatsApp *"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="bg-black/40 backdrop-blur-sm border-white/20 text-white placeholder-gray-400 focus:border-blue-400 focus:ring-blue-400 focus:bg-black/60 transition-all duration-300"
                    />
                  </div>
                  <div>
                    <Input
                      name="company"
                      placeholder="Nama Bisnis"
                      value={formData.company}
                      onChange={handleChange}
                      className="bg-black/40 backdrop-blur-sm border-white/20 text-white placeholder-gray-400 focus:border-blue-400 focus:ring-blue-400 focus:bg-black/60 transition-all duration-300"
                    />
                  </div>
                </div>

                <div>
                  <Textarea
                    name="message"
                    placeholder="Ceritakan tentang bisnis Anda dan kebutuhan yang ingin diselesaikan..."
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="bg-black/40 backdrop-blur-sm border-white/20 text-white placeholder-gray-400 focus:border-blue-400 focus:ring-blue-400 focus:bg-black/60 transition-all duration-300"
                  />
                </div>

                <Button 
                  type="submit"
                  className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white py-3 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105"
                >
                  <Send className="mr-2 h-5 w-5" />
                  Kirim Pesan & Dapatkan Demo
                </Button>
              </form>
            </CardContent>
            </div>
          </Card>

          {/* Contact Info */}
          <div className="space-y-8">
            {/* Quick Contact */}
            <Card className="p-6 bg-black/60 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/40 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-lg"></div>
              <div className="relative z-10">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center">
                  <MessageCircle className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">WhatsApp Langsung</h3>
                  <p className="text-gray-300">Respon cepat dalam 5 menit</p>
                </div>
              </div>
              <Button 
                className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-full font-semibold"
                onClick={() => window.open('https://api.whatsapp.com/send/?phone=6281218691110&text=Halo%20BTSSOFT%2C%20saya%20ingin%20konsultasi%20mengenai%20XAI%20ERP%20System.%20Mohon%20informasi%20lebih%20lanjut.&type=phone_number&app_absent=0', '_blank')}
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Chat via WhatsApp
              </Button>
              </div>
            </Card>

            {/* Contact Details */}
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-500/20 text-blue-400 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-semibold text-white">Telepon</h4>
                  <p className="text-gray-300">+6281218691110</p>
                  <p className="text-sm text-gray-400">Senin-Sabtu 09:00 - 17:00 WIB</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-semibold text-white">Email</h4>
                  <p className="text-gray-300">support@btssoft.com</p>
                  <p className="text-sm text-gray-400">Respon dalam 2-4 jam</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-purple-500/20 text-purple-400 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-semibold text-white">Alamat</h4>
                  <p className="text-gray-300">Medan, Indonesia</p>
                  <p className="text-sm text-gray-400">Kunjungi kantor dengan appointment</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-orange-500/20 text-orange-400 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-semibold text-white">Jam Operasional</h4>
                  <p className="text-gray-300">Senin-Sabtu 09:00 - 17:00 WIB</p>
                  <p className="text-sm text-gray-400">Support 24/7 untuk klien premium</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;